import React, { useCallback, VoidFunctionComponent } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { ManageEntitiesContentLayout } from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { useTranslation } from 'react-i18next';
import { EntityName } from 'record-it-time-common/EntityName';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { TimeTrackingConfigurationList } from '../../timeTrackingConfiguration/TimeTrackingConfigurationList/TimeTrackingConfigurationList';
import { TimeTrackingConfiguration } from '../../../classes/entities/TimeTrackingConfiguration/timeTrackingConfigurationTypes';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useUserGroupCreateEntityButtonConfig } from '../../../hooks/entity/useUserGroupCreateEntityButtonConfig';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

export type TimeTrackingConfigurationAdminPageProps = {
  children?: Array<never>;
};

export const TimeTrackingConfigurationAdminPage: VoidFunctionComponent<
  TimeTrackingConfigurationAdminPageProps
> = () => {
  const timeTrackingConfigurationRepository = useEntityRepository(
    EntityName.TimeTrackingConfiguration
  );
  const timeTrackingConfigurations = useEntities(
    EntityName.TimeTrackingConfiguration,
    'getAll'
  );
  const { t } = useTranslation();

  const { getRequiredCurrentUserPermissions } = useCurrentUser();

  useConfigureBreadcrumbsWithBuilder((builder) =>
    builder.addHomePage().addTimeTrackingConfigurationAdminPage()
  );

  const createConfiguration = useCallback(
    (userGroup: UserGroup | null) => {
      assertNotNullOrUndefined(
        userGroup,
        "can't create a timeTrackingConfiguration without a usergroup"
      );

      timeTrackingConfigurationRepository.create({
        maxWorkHoursPerDay: 10,
        ownerUserGroupId: userGroup.id
      });
    },
    [timeTrackingConfigurationRepository]
  );

  const renderEntities = useCallback(
    (entities: Array<TimeTrackingConfiguration>) => {
      return (
        <TimeTrackingConfigurationList
          timeTrackingConfigurations={entities}
        ></TimeTrackingConfigurationList>
      );
    },
    []
  );

  const createEntityButtonConfig = useUserGroupCreateEntityButtonConfig({
    text: t('components.page.TimeTrackingConfigurationAdminPage.createEntity'),
    userGroups:
      getRequiredCurrentUserPermissions().getEditableUserGroupsForTimeTrackingConfigurations(),
    onCreateEntity: createConfiguration
  });

  return (
    <ManageEntitiesContentLayout
      headerConfig={{
        title: t(
          'components.page.TimeTrackingConfigurationAdminPage.pageTitle'
        ),
        iconConfig:
          iconConfigurations.prefixed.page.TimeTrackingConfigurationAdminPage
            .titleIcon
      }}
      entities={timeTrackingConfigurations}
      renderEntities={renderEntities}
      createEntityButtonConfig={createEntityButtonConfig}
    ></ManageEntitiesContentLayout>
  );
};
